/**
 * CLE-2811 — under a contract's header: what its latest contract model is doing, and a link to open it.
 * Super admin only — the caller's gate, like the tool's route and every endpoint behind it.
 *
 * While a pass is in flight it polls the slim progress every 1.5 s, the way the run page does
 * (`livePolling.ts`), to say what the worker is doing and for how long, and it refetches the latest model
 * when that pass ends or saves its facts (`shouldRefetchLatest`). Nothing polls once it has settled.
 */

import { OPEN_MODEL } from "pages/ContractModelDrafts/copy";
import {
  PROGRESS_POLL_MS,
  RUN_SAFETY_REFETCH_MS,
  freshProgress,
} from "pages/ContractModelDrafts/livePolling";
import { isProcessing } from "pages/ContractModelDrafts/utils";
import { Message } from "primereact/message";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  useGetContractModelDraftRunProgressQuery,
  useGetLatestContractModelRunQuery,
} from "store/queries/contractModelDrafts";

import {
  ContractModelBannerKind,
  IContractModelBanner,
  contractModelBannerOf,
  contractModelPath,
  liveLatestOf,
  liveProgressLine,
  shouldRefetchLatest,
} from "../contractModel";

const ICONS: Record<ContractModelBannerKind, string> = {
  reading: "pi pi-spin pi-spinner",
  facts_ready: "pi pi-spin pi-spinner",
  found: "pi pi-sparkles",
  failed: "pi pi-exclamation-triangle",
  saved_here: "pi pi-link",
};

interface IContractModelBannerViewProps {
  banner: IContractModelBanner;
  runId: string;
  /** This page's URL: the contract model's back button returns here. */
  returnTo: string;
  /** While a pass reads: what the worker is doing, and for how long. */
  live?: { phase: string; elapsed: string | null } | null;
}

/** The banner itself — one line: the state, the live phase while it reads, and "Open". */
export function ContractModelBannerView({
  banner,
  runId,
  returnTo,
  live = null,
}: IContractModelBannerViewProps) {
  return (
    <Message
      severity={banner.severity}
      className="w-full justify-content-start mb-4"
      content={
        <div
          className="flex align-items-center flex-wrap gap-2"
          data-testid="contract-model-banner"
        >
          <i className={ICONS[banner.kind]} aria-hidden />
          <span className="font-medium">{banner.text}</span>
          {live ? (
            <span className="text-sm">
              {live.phase}
              {live.elapsed ? (
                <>
                  {" "}
                  · <strong>{live.elapsed}</strong>
                </>
              ) : null}
            </span>
          ) : null}
          <span aria-hidden>·</span>
          <Link
            to={contractModelPath(runId)}
            state={{ from: returnTo }}
            className="font-medium"
          >
            {OPEN_MODEL}
          </Link>
        </div>
      }
    />
  );
}

interface IContractModelBannerProps {
  contractId: string;
  returnTo: string;
}

export function ContractModelBanner({
  contractId,
  returnTo,
}: IContractModelBannerProps) {
  const [processing, setProcessing] = useState(false);
  const { data: latest, refetch } = useGetLatestContractModelRunQuery(
    contractId,
    { pollingInterval: processing ? RUN_SAFETY_REFETCH_MS : 0 }
  );
  useEffect(() => {
    setProcessing(!!latest && isProcessing(latest.status));
  }, [latest]);

  // When this pass started as far as this page knows: a slim response fetched before it is a cached tick
  // of an earlier pass (`freshProgress`).
  const passSinceRef = useRef<number | null>(null);
  if (processing && passSinceRef.current === null)
    passSinceRef.current = Date.now();
  if (!processing && passSinceRef.current !== null) passSinceRef.current = null;
  const { data: progressData, fulfilledTimeStamp } =
    useGetContractModelDraftRunProgressQuery(latest?.id ?? "", {
      skip: !latest || !processing,
      pollingInterval: processing ? PROGRESS_POLL_MS : 0,
      refetchOnMountOrArgChange: true,
    });
  const slim = freshProgress(
    progressData,
    fulfilledTimeStamp,
    passSinceRef.current
  );
  useEffect(() => {
    if (shouldRefetchLatest(latest, slim)) refetch();
  }, [latest, slim, refetch]);

  const live = liveLatestOf(latest, slim);
  const banner = contractModelBannerOf(live);

  // The elapsed time ticks every second while it reads.
  const [now, setNow] = useState(() => Date.now());
  const ticking = !!banner?.live;
  useEffect(() => {
    if (!ticking) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [ticking]);

  if (!banner || !live) return null;
  return (
    <ContractModelBannerView
      banner={banner}
      runId={live.id}
      returnTo={returnTo}
      live={
        banner.live && slim && slim.id === live.id
          ? liveProgressLine(slim, now)
          : null
      }
    />
  );
}

export default ContractModelBanner;
